import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Row, Col, FormGroup, Label, Input, Button,
  Card, CardBody, CardHeader, FormFeedback, Badge
} from 'reactstrap';
import serverRequest from '../../../utils/serverRequest';
import './Atena.css';

// helper simples pra ler ?categoria=...
function useQuery() {
  const qs = typeof window !== 'undefined' ? window.location.hash.split('?')[1] || '' : '';
  const pairs = new URLSearchParams(qs);
  return { get: (k) => pairs.get(k) };
}

const palette = {
  headerGrad: 'linear-gradient(135deg, #0a0f2b 0%, #1b2357 45%, #4b2b8a 100%)',
  cardBg: '#0f1730',
  cardBorder: '#1c2a57',
  text: '#eaeefb',
  subText: '#b9c2f2',
  inputBg: '#0d1530',
  inputBorder: '#2b3f7a',
  inputBorderFocus: '#6c5ce7',
  primary: '#6c5ce7',
  primaryHover: '#5b4dd0'
};

export default function AtenaNovoChamado() {
  const q = useQuery();
  const catFromURL = q.get('categoria') || '';

  // categorias vindas do backend
  const [categorias, setCategorias] = useState([]);
  const categoriasFallback = useMemo(() => ([
    { id: 1, nome: 'Criar Usuario' },
    { id: 2, nome: 'Liberação de Acesso' },
    { id: 3, nome: 'Criação de Report' },
    { id: 4, nome: 'Criação de Gravação' },
    { id: 5, nome: 'Erro de Gravação' },
    { id: 6, nome: 'Erro de Report' },
    { id: 7, nome: 'Reset de Senha' },
    { id: 8, nome: 'Falha Diario de Bordo' },
    { id: 9, nome: 'Falha no DataPulse' },
    { id: 10, nome: 'Criação de Novos Processos' },
    { id: 99, nome: 'Outros' },
  ]), []);

  const [form, setForm] = useState({
    categoria: catFromURL,
    prioridade: 'Média',
    titulo: '',
    caminho: '',
    descricao: ''
  });

  const [touchedSubmit, setTouchedSubmit] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState('');

  const valid = {
    categoria: !!form.categoria,
    prioridade: !!form.prioridade,
    titulo: form.titulo.trim().length >= 3,       // TÍTULO OBRIGATÓRIO
    caminho: form.caminho.trim().length > 2,
    descricao: form.descricao.trim().length >= 10 // mín. 10
  };
  const canSave = Object.values(valid).every(Boolean);

  // GET categorias do backend
  async function carregarCategorias() {
    try {
      const list = await serverRequest.get('/atena/categorias'); // GET
      setCategorias(Array.isArray(list) ? list : categoriasFallback);
    } catch {
      setCategorias(categoriasFallback);
    }
  }
  useEffect(() => { carregarCategorias(); /* eslint-disable-next-line */ }, []);

  // helper para extrair usuário do localStorage
  function getLocalUser() {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return {};
      const u = JSON.parse(raw);
      return {
        id: u?.id_usuario ?? u?.id ?? null,
        nome: u?.nome || null,
        email: u?.ds_usuario || u?.email || null,
      };
    } catch {
      return {};
    }
  }

  // POST criar chamado (CORRIGIDO: Forçando POST se possível, ou mantendo request genérico)
  async function criarChamado(data) {
    // Tenta usar .post se existir no seu utils, senão usa .request
    if (serverRequest.post) {
        return await serverRequest.post('/atena/chamados/criar', data);
    }
    return await serverRequest.request('/atena/chamados/criar', data);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouchedSubmit(true); // Ativa as mensagens de erro nos inputs
    setMsg('');

    // 1. Validação Visual: Se tiver erro, para aqui e avisa o usuário
    if (!canSave) {
      setMsg('⚠️ Preencha todos os campos obrigatórios marcados em vermelho.');
      return;
    }

    if (enviando) return;

    const localUser = getLocalUser();
    const solicitante_nome  = localUser.nome || localUser.email || undefined;
    const solicitante_email = localUser.email || undefined;

    setEnviando(true);
    try {
      const payload = {
        categoria: form.categoria,
        titulo: form.titulo,
        descricao: form.descricao,
        caminho: form.caminho,
        prioridade: form.prioridade,
        solicitante_nome,
        solicitante_email
      };

      console.log('🛫 Enviando chamado:', payload); // DEBUG

      const out = await criarChamado(payload);
      
      console.log('🛬 Resposta do servidor:', out); // DEBUG

      // Verificação mais flexível do ID (backend as vezes muda o nome do campo)
      const ticketId = out?.ticket_id || out?.id_ticket || out?.id || out?.data?.id;

      if (ticketId || (out && (out.ok || out.success))) {
        setMsg('✅ Chamado aberto com sucesso! Redirecionando...');
        
        // Pequeno delay para usuário ler a mensagem
        setTimeout(() => {
            if (ticketId) {
                window.location.href = `#/atena/tratamento/${ticketId}`;
            } else {
                // Se salvou mas não veio ID, volta pra lista
                window.location.href = `#/atena/meus-chamados`;
            }
        }, 1500);
        return;
      }
      
      setMsg('❌ O servidor não retornou confirmação.');
    } catch (err) {
      console.error('Falha ao criar chamado:', err);
      setMsg('❌ Erro técnico ao abrir chamado (Verifique o Console).');
    } finally {
      setEnviando(false);
    }
  }

  const inputBaseStyle = {
    background: palette.inputBg, color: palette.text, border: `1px solid ${palette.inputBorder}`
  };
  const headerStyle = {
    background: palette.headerGrad, color: palette.text, borderBottom: 'none', padding: '20px 24px'
  };
  const cardStyle = {
    background: palette.cardBg, color: palette.text,
    border: `1px solid ${palette.cardBorder}`, borderRadius: 14,
    boxShadow: '0 14px 40px rgba(16, 24, 64, .35)'
  };
  const labelStyle = { fontWeight: 600, color: palette.subText };
  const reqStar = <span style={{ color: palette.primary, marginLeft: 4 }}>*</span>;

  return (
    <div style={{
      backgroundImage: "url('../img/bg-gorila.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
    }}>
      <div className="atena-page">
        <Container className="mt-4">
          <Row>
            <Col md={12} lg={10} xl={12} className="mx-auto">
              <Card style={cardStyle}>
                <CardHeader style={headerStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <span style={{
                      display: 'inline-flex', width: 36, height: 36, borderRadius: 10,
                      alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.12)'
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                        <path d="M12 2c-4.97 0-9 4.03-9 9v6a2 2 0 0 0 2 2h2v-4H5v-4c0-3.87 3.13-7 7-7s7 3.13 7 7v4h-2v4h2a2 2 0 0 0 2-2v-6c0-4.97-4.03-9-9-9zm-1 9H8v2h3v5h2v-5h3v-2h-3V7h-2v4z"/>
                      </svg>
                    </span>
                    <div>
                      <h4 className="mb-0" style={{ fontWeight: 800, letterSpacing: 1 }}>ATENA • Novo Chamado</h4>
                      <small style={{ color: palette.subText }}>Descreva o problema para que possamos tratar com agilidade.</small>
                    </div>
                  </div>
                  {!!form.categoria && (
                    <Badge style={{ background: 'rgba(255,255,255,.18)' }} pill>
                      Categoria selecionada: {form.categoria}
                    </Badge>
                  )}
                </CardHeader>

                <CardBody style={{ paddingTop: 22 }}>
                  <form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label style={labelStyle}>Categoria {reqStar}</Label>
                          <Input
                            type="select"
                            value={form.categoria}
                            onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                            style={inputBaseStyle}
                            invalid={touchedSubmit && !valid.categoria}
                            onFocus={(e) => (e.target.style.border = `1px solid ${palette.inputBorderFocus}`)}
                            onBlur={(e) => (e.target.style.border = `1px solid ${palette.inputBorder}`)}
                          >
                            <option value="">Selecione…</option>
                            {(categorias.length ? categorias : categoriasFallback).map(c =>
                              <option key={c.id} value={c.nome}>{c.nome}</option>
                            )}
                          </Input>
                          <FormFeedback>Escolha uma categoria.</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label style={labelStyle}>Prioridade {reqStar}</Label>
                          <Input
                            type="select"
                            value={form.prioridade}
                            onChange={(e) => setForm({ ...form, prioridade: e.target.value })}
                            style={inputBaseStyle}
                            invalid={touchedSubmit && !valid.prioridade}
                            onFocus={(e) => (e.target.style.border = `1px solid ${palette.inputBorderFocus}`)}
                            onBlur={(e) => (e.target.style.border = `1px solid ${palette.inputBorder}`)}
                          >
                            <option>Baixa</option>
                            <option>Média</option>
                            <option>Alta</option>
                            <option>Crítica</option>
                          </Input>
                          <FormFeedback>Selecione a prioridade.</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup>
                          <Label style={labelStyle}>Título {reqStar}</Label>
                          <Input
                            value={form.titulo}
                            maxLength={140}
                            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                            placeholder="Ex.: Erro 500 ao baixar gravação no Kroton"
                            style={inputBaseStyle}
                            invalid={touchedSubmit && !valid.titulo}
                            onFocus={(e) => (e.target.style.border = `1px solid ${palette.inputBorderFocus}`)}
                            onBlur={(e) => (e.target.style.border = `1px solid ${palette.inputBorder}`)}
                          />
                          <FormFeedback>Informe um título (mínimo 3 caracteres).</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup>
                          <Label style={labelStyle}>Caminho / Tela onde ocorreu {reqStar}</Label>
                          <Input
                            value={form.caminho}
                            onChange={(e) => setForm({ ...form, caminho: e.target.value })}
                            placeholder="Ex.: Menu > Aspect > Gravações • URL: /aspect/download-pcm"
                            style={inputBaseStyle}
                            invalid={touchedSubmit && !valid.caminho}
                            onFocus={(e) => (e.target.style.border = `1px solid ${palette.inputBorderFocus}`)}
                            onBlur={(e) => (e.target.style.border = `1px solid ${palette.inputBorder}`)}
                          />
                          <FormFeedback>Descreva o caminho/tela onde ocorreu.</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup>
                          <Label style={labelStyle}>Descreva a falha {reqStar}</Label>
                          <Input
                            type="textarea"
                            rows={6}
                            value={form.descricao}
                            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                            placeholder="Passos para reproduzir, mensagens de erro, data/hora, etc."
                            style={{ ...inputBaseStyle, resize: 'vertical' }}
                            invalid={touchedSubmit && !valid.descricao}
                            onFocus={(e) => (e.target.style.border = `1px solid ${palette.inputBorderFocus}`)}
                            onBlur={(e) => (e.target.style.border = `1px solid ${palette.inputBorder}`)}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <FormFeedback>Descreva a falha (mínimo 10 caracteres).</FormFeedback>
                            <small style={{ color: palette.subText, marginTop: 6 }}>
                              {form.descricao.length}/10000
                            </small>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <Button
                        type="button"
                        onClick={() => window.history.back()}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${palette.inputBorder}`,
                          color: palette.text
                        }}
                      >
                        Voltar
                      </Button>

                      {/* CORRIGIDO: Botão agora só desabilita se estiver enviando. Se estiver inválido, ele clica e mostra o erro. */}
                      <Button
                        type="submit"
                        disabled={enviando} 
                        style={{
                          background: palette.primary,
                          border: 'none',
                          color: '#fff',
                          fontWeight: 700,
                          cursor: enviando ? 'wait' : 'pointer',
                          opacity: enviando ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = palette.primaryHover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = palette.primary)}
                      >
                        {enviando ? 'Enviando…' : 'Abrir Chamado'}
                      </Button>
                    </div>

                    {msg && (
                      <p className={`message-box ${msg.includes('✅') ? 'success' : 'error'}`} 
                         style={{ 
                             marginTop: 16, 
                             color: msg.includes('✅') ? '#4cd137' : '#e84118',
                             fontWeight: 600 
                         }}>
                        {msg}
                      </p>
                    )}
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}